import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './Grades.module.css';
import apiClient from '../../api/client';
import { stages } from '../../data/studentsData';

const Grades = () => {
  const [exams, setExams] = useState([]);
  const [selectedStage, setSelectedStage] = useState(''); // فلتر المرحلة الدراسية عشان منع اللخبطة بين الامتحانات
  const [selectedExam, setSelectedExam] = useState('');
  const [students, setStudents] = useState([]);
  const [gradesData, setGradesData] = useState({});
  const [onlineGrades, setOnlineGrades] = useState({}); // للعرض بس (الدرجات الأونلاين بتتحسب تلقائي)
  const [saving, setSaving] = useState(false);

  // 1. جلب قائمة الامتحانات والطلاب عند فتح الصفحة
  useEffect(() => {
    apiClient.get('/exams')
      .then(res => setExams(res.data))
      .catch(err => console.error("Error fetching exams:", err));

    apiClient.get('/students')
      .then(res => setStudents(res.data))
      .catch(err => console.error("Error fetching students:", err));
  }, []);

  // امتحانات المرحلة المختارة بس - عشان مايظهرش امتحانات مراحل تانية تلخبط المدرس
  const stageFilteredExams = useMemo(() => {
    if (!selectedStage) return exams;
    return exams.filter(exam => exam.stage === selectedStage);
  }, [exams, selectedStage]);

  // لو غيرنا المرحلة وكان فيه امتحان متختار مش تابع للمرحلة الجديدة، بنصفّره
  useEffect(() => {
    if (selectedExam && !stageFilteredExams.some(e => e._id === selectedExam)) {
      setSelectedExam('');
    }
  }, [selectedStage, stageFilteredExams, selectedExam]);

  // بنعرض بس طلاب مرحلة الامتحان المختار
  const selectedExamData = exams.find(e => e._id === selectedExam);
  const filteredStudents = useMemo(() => {
    if (!selectedExamData) return students;
    return students.filter(s => s.grade === selectedExamData.stage);
  }, [students, selectedExamData]);

  // 2. جلب الدرجات الورقية المسجلة قبل كده (لعرضها جاهزة) + الدرجات الأونلاين (للعرض فقط)
  const loadExistingGrades = useCallback(async (examId) => {
    if (!examId) {
      setGradesData({});
      setOnlineGrades({});
      return;
    }
    try {
      const [paperRes, onlineRes] = await Promise.all([
        apiClient.get(`/grades?exam=${examId}&type=paper`),
        apiClient.get(`/grades?exam=${examId}&type=online`),
      ]);
      const paperMap = {};
      paperRes.data.forEach(g => { if (g.student) paperMap[g.student._id] = g.score; });
      setGradesData(paperMap);

      const onlineMap = {};
      onlineRes.data.forEach(g => { if (g.student) onlineMap[g.student._id] = g.score; });
      setOnlineGrades(onlineMap);
    } catch (error) {
      console.error("Error loading existing grades:", error);
    }
  }, []);

  useEffect(() => {
    loadExistingGrades(selectedExam);
  }, [selectedExam, loadExistingGrades]);

  // تحديث الدرجة في الـ State
  const handleScoreChange = (studentId, score) => {
    setGradesData(prev => ({
      ...prev,
      [studentId]: score
    }));
  };

  // 3. حفظ الدرجات الورقية (اللي اتحلت وصُححت يدوي في السنتر)
  const handleSaveGrades = async () => {
    if (!selectedExam) return alert("من فضلك اختر الامتحان أولاً!");

    const gradesList = Object.keys(gradesData).map(studentId => ({
      student: studentId,
      exam: selectedExam,
      score: gradesData[studentId],
      type: 'paper',
    }));

    setSaving(true);
    try {
      await apiClient.post('/grades', gradesList);
      alert("تم رصد الدرجات الورقية بنجاح! 🎉");
    } catch (error) {
      console.error("Error saving grades:", error);
      alert("حدث خطأ أثناء الحفظ.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>رصد درجات الامتحانات الورقية</h1>
        <button className={styles.saveBtn} onClick={handleSaveGrades} disabled={saving}>
          {saving ? 'جاري الحفظ...' : 'حفظ الدرجات'}
        </button>
      </div>

      <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '-0.5rem 0 0.5rem' }}>
        الصفحة دي لرصد درجات الامتحان الورقي اللي بيتحل في السنتر يدوياً. درجات الامتحان الأونلاين بتتحسب تلقائي وبتظهر هنا للمراجعة بس.
      </p>

      <div className={styles.filtersContainer} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <select
          className={styles.selectBox}
          value={selectedStage}
          onChange={(e) => setSelectedStage(e.target.value)}
        >
          <option value="">كل المراحل الدراسية</option>
          {stages.map((stage, index) => (
            <option key={index} value={stage}>{stage}</option>
          ))}
        </select>

        <select
          className={styles.selectBox}
          value={selectedExam}
          onChange={(e) => setSelectedExam(e.target.value)}
        >
          <option value="">اختر الامتحان</option>
          {stageFilteredExams.map(exam => (
            <option key={exam._id} value={exam._id}>
              {exam.name} - {exam.subject} ({exam.stage})
            </option>
          ))}
        </select>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>كود الطالب</th>
              <th>الاسم</th>
              <th>درجة الورقي</th>
              <th>درجة الأونلاين</th>
            </tr>
          </thead>
                <tbody>
                {filteredStudents.map(student => (
                    <tr key={student._id}>
                    <td data-label="الكود:">{student.code}</td>
                    <td data-label="الاسم:">{student.name}</td>
                    <td data-label="درجة الورقي:">
                        <input
                        type="number"
                        className={styles.formInput}
                        value={gradesData[student._id] ?? ''}
                        onChange={(e) => handleScoreChange(student._id, e.target.value)}
                        />
                    </td>
                    <td data-label="درجة الأونلاين:">
                      {onlineGrades[student._id] !== undefined ? onlineGrades[student._id] : '—'}
                    </td>
                    </tr>
                ))}
                </tbody>
        </table>
      </div>
    </div>
  );
};

export default Grades;
